import {
  FilterDescriptor,
  LooopS3BucketEnum,
  OrderDescriptor,
  PathwayInput,
  PathwayProgress,
  SqlOrderDirectionEnum
} from 'front-end/src/graphql/private/PrivateSchemaTypes'
import _ from 'lodash'
import React, { ReactNode, useState } from 'react'
import { Route, Switch, useParams } from 'react-router-dom'
import { Box, Flex } from 'front-end/src/components'
import { linkBuilder } from 'shared/linkBuilder'
import Loader from '../../components/common/loader/Loader'
import { Page } from '../../components/common/page/Page'
import { apolloClientPrivate } from '../../graphql/apolloClient'
import pathwaySetTopics from '../../operations/pathwaySetTopics'
import { PathwayResourcesPlural } from '../manage-pathways-page/components/PathwayItem'
import PathwayAccessTab from './access-tab/AccessTab'
import {
  PathwayPatchDocument,
  PathwayPatchMutation,
  PathwayPatchMutationVariables,
  PathwayWithTopicsQuery,
  PathwayWithTopicsQueryVariables,
  usePathwayAccessGroupsQuery,
  usePathwayAccessUsersQuery,
  usePathwayWithTopicsQuery
} from './ManagePathwayPage.codegen'
import { PathwayResourcesTab } from './resources-tab/PathwayResourcesTab'
import './ManagePathwayPage.less'
import { ManagePathwayTopbar } from './components/ManagePathwayTopbar'
import { Editable, EditableInput, EditablePreview } from '@chakra-ui/react'
import { brandColors, formColors } from 'shared/basicPalette'
import { t, Trans } from '@lingui/macro'
import { PathwayByline } from '../pathway-detail-page/components/PathwayByline'
import ImageFormat from '../topic-detail-page/components/text-element/ImageFormat'
import ImageResize from '@looop/quill-image-resize-module-react'
import ImageUploader from '@looop/quill-image-uploader'
import { LooopS3FileUpload } from '../../app/util/LooopS3FileUpload'
import { replaceBase64ImagesInHtml } from '../topic-detail-page/components/utils'
import EditorQuill from '../../components/common/quill/EditorQuill'
import { Quill } from '@capaj/react-quill'
import IconExclamation from '../../components/common/icon/IconExclamation'
import * as ApolloReactHooks from '@apolloHooksWithThrowingQuery'
import { useReorderPathwayResources } from './useReorderPathwayResources'
import { DEFAULT_PAGE_LIMIT } from 'front-end/src/components/common/table-extended/TableExtended'

type State = {
  settingsForm: PathwayInput
  userOrder: OrderDescriptor
  groupOrder: OrderDescriptor
  pageSize: number
  page: number
  filters: FilterDescriptor[]
}

export enum SavingStatus {
  EMPTY,
  SAVING,
  SAVED
}

Quill.register(ImageFormat, true)
Quill.register('modules/imageUploader', ImageUploader)
Quill.register('modules/imageResize', ImageResize)

export const useManagePathwayWithTopicsQuery = (
  baseOptions: ApolloReactHooks.QueryHookOptions<
    PathwayWithTopicsQuery,
    PathwayWithTopicsQueryVariables
  >
) => {
  return usePathwayWithTopicsQuery(baseOptions)
}

export const ManagePathwayPage = () => {
  const [description, setDescription] = useState('')
  const { pathwayId } = useParams<{ pathwayId: string }>()
  const [savingStatus, setSavingStatus] = useState<SavingStatus>(
    SavingStatus.EMPTY
  )
  const {
    data: pathwayData,
    loading: pathwayLoading,
    refetch: refetchPathway
  } = useManagePathwayWithTopicsQuery({
    variables: {
      id: Number(pathwayId)
    },
    fetchPolicy: 'cache-first',
    onCompleted: (data) =>
      setDescription(data?.admin?.pathway?.description || '')
  })

  const [state, setState] = useState<State>({
    userOrder: { name: 'display_name', sort: SqlOrderDirectionEnum.ASC },
    groupOrder: { name: 'name', sort: SqlOrderDirectionEnum.ASC },
    page: 0,
    pageSize: DEFAULT_PAGE_LIMIT,
    filters: [
      {
        name: 'is_archived',
        value: false
      },
      {
        name: 'display_name',
        value: ''
      }
    ],
    settingsForm: {
      title: '',
      description: ''
    }
  })

  const {
    data: usersData,
    loading: usersLoading,
    refetch: usersRefetch
  } = usePathwayAccessUsersQuery({
    variables: {
      id: Number(pathwayId),
      limit: state.pageSize,
      offset: state.page * state.pageSize,
      order: state.userOrder,
      filters: state.filters as any // TODO
    }
  })

  React.useEffect(() => {
    usersRefetch()
  }, [state.filters])

  const {
    data: groupsData,
    loading: groupsLoading,
    refetch: groupsRefetch
  } = usePathwayAccessGroupsQuery({
    variables: {
      id: Number(pathwayId),
      order: state.groupOrder
    }
  })

  const pathway = pathwayData?.admin?.pathway

  const { topics: pathwayResources, handleChangeOrder } =
    useReorderPathwayResources(
      pathway?.currentTopicVersions ?? null,
      pathway?.id ?? 0,
      setSavingStatus,
      refetchPathway
    )

  const updateState = (changes: Partial<State>) => {
    setState({ ...state, ...changes })
  }

  const handleTopicRemoved = async (topicId: number): Promise<void> => {
    setSavingStatus(SavingStatus.SAVING)
    if (pathway) {
      const topicIds = pathway.currentTopicVersions.map(
        (currentTopicVersion) => currentTopicVersion.topic.id
      )
      topicIds.splice(topicIds.indexOf(topicId), 1)

      await pathwaySetTopics(pathway.id, topicIds)

      const cloned = _.cloneDeep(pathway)
      cloned.currentTopicVersions = cloned.currentTopicVersions.filter(
        (tv) => tv.topic.id !== topicId
      )
      setSavingStatus(SavingStatus.SAVED)
      refetchPathway()
    }
  }

  const handleUserPageChange = (page: number) => {
    updateState({
      page
    })
  }

  const handleUsersOrderChange = (order: any) => {
    updateState({
      userOrder: {
        name: order[0].id,
        sort: order[0].desc
          ? SqlOrderDirectionEnum.DESC
          : SqlOrderDirectionEnum.ASC
      }
    })
  }

  const handleGroupsOrderChange = (order: any) => {
    updateState({
      groupOrder: {
        name: order[0].id,
        sort: order[0].desc
          ? SqlOrderDirectionEnum.DESC
          : SqlOrderDirectionEnum.ASC
      }
    })
  }

  const handleSearchChanged = (search: string) => {
    const prevFilters = state.filters
    const keyName = 'display_name'
    prevFilters.find(function (value, index) {
      if (value.name === keyName) {
        prevFilters[index].value = search
      }
    })
    updateState({ filters: prevFilters, page: 0 })
    usersRefetch()
  }

  const sendPathwayPatch = _.debounce(
    async (patch: PathwayInput, refetch = true) => {
      setSavingStatus(SavingStatus.SAVING)
      await apolloClientPrivate.mutate<
        PathwayPatchMutation,
        PathwayPatchMutationVariables
      >({
        mutation: PathwayPatchDocument,
        variables: { id: Number(pathwayId), input: patch },
        onQueryUpdated: () => {
          setSavingStatus(SavingStatus.SAVED)
          refetch ? refetchPathway() : () => null
        }
      })
    },
    200
  )

  const handlePathwayPublicChanged = (isPublic: boolean) => {
    sendPathwayPatch({
      is_public: isPublic
    })
  }

  const renderResourceTabControls = (): ReactNode => {
    if (!pathway) {
      return null
    }

    if (
      pathway?.currentTopicVersionsCount &&
      pathway.currentTopicVersionsCount < 1
    ) {
      return null
    }

    return (
      <h3>
        <PathwayResourcesPlural value={pathway.currentTopicVersionsCount} />
      </h3>
    )
  }

  const renderResourcesTab = (): ReactNode => {
    if (pathway) {
      return (
        <>
          <Box w="66%" mb="48px" px="16px">
            <Editable
              mb="8px"
              placeholder={t`Pathway title`}
              fontSize="40px"
              minH="56px"
              lineHeight="56px"
              defaultValue={pathway.title}
              onSubmit={(value) => sendPathwayPatch({ title: value }, false)}
            >
              <EditablePreview w="100%" />
              <EditableInput minH="56px" bgColor={formColors.bgColor} />
            </Editable>

            <Box mb="8px">
              <PathwayByline
                publishedDate={pathway.published_date}
                updatedDate={pathway.updated_at}
                resourcesCount={pathway.currentTopicVersionsCount || 0}
                version={pathway.currentPathwayVersion.version}
                isArchived={false}
              />
            </Box>

            <EditorQuill
              value={description}
              uploadFn={async (fileName, blob) => {
                return LooopS3FileUpload({
                  S3UploadProps: {
                    filename: fileName,
                    S3path: `pathway-description-images/pathway-${pathway.id}`,
                    bucket: LooopS3BucketEnum.PRIVATE
                  },
                  blob: blob
                })
              }}
              onChange={async (content) => {
                const html = await replaceBase64ImagesInHtml(
                  content,
                  {
                    S3path: `pathway-description-images/pathway-${pathway.id}`,
                    bucket: LooopS3BucketEnum.PRIVATE
                  },
                  () => {}
                )

                setDescription(html)
                sendPathwayPatch({ description: html }, false)
              }}
            />
            <Flex align="center" mt="12px" color={brandColors.grey['500']}>
              <IconExclamation mr="6px" />
              <Trans>
                Currently only adding or removing resources will create new
                versions.
              </Trans>
            </Flex>
          </Box>
          <PathwayResourcesTab
            pathwayTopics={pathway}
            pathwayControls={renderResourceTabControls()}
            loading={pathwayLoading}
            fixedOrder={
              pathway?.progress === PathwayProgress.linear ||
              pathway?.progress === PathwayProgress.locked
            }
            onTopicRemoved={handleTopicRemoved}
            onTopicOrderChange={handleChangeOrder}
            refetchTopics={refetchPathway}
            resources={pathwayResources}
          />
        </>
      )
    }
  }

  const { page, pageSize } = state

  if (pathwayLoading || !pathway) {
    return <Loader />
  }

  return (
    <>
      <ManagePathwayTopbar
        savingStatus={savingStatus}
        pathway={pathway}
        sendPathwayPatch={sendPathwayPatch}
        onShare={() => {
          usersRefetch()
          groupsRefetch()
        }}
      />
      <Page withTopbar className="PathwayTopics">
        <div className="PathwayTopics__body">
          <div className="PathwayTopics__bodyInner">
            <Switch>
              <Route
                exact
                path={linkBuilder.pathwayDetailManage(Number(pathwayId))}
                render={() => renderResourcesTab()}
              />
              <Route
                path={linkBuilder.pathwayDetailManageMembers(Number(pathwayId))}
                render={() => (
                  <PathwayAccessTab
                    isPathwayPublished={!!pathway.published_date}
                    pathwayPublic={!!pathway?.is_public}
                    page={page}
                    pageSize={pageSize}
                    onPageChange={handleUserPageChange}
                    onUsersChanged={usersRefetch}
                    onGroupsChanged={groupsRefetch}
                    onSearchChange={handleSearchChanged}
                    onUsersOrderChange={handleUsersOrderChange}
                    onGroupsOrderChange={handleGroupsOrderChange}
                    onPathwayPublicChanged={handlePathwayPublicChanged}
                    loadingGroups={groupsLoading}
                    loadingUsers={usersLoading}
                    pathwayGroups={groupsData?.admin?.pathway?.groups}
                    pathwayUsers={usersData?.admin?.pathway}
                  />
                )}
              />
            </Switch>
          </div>
        </div>
      </Page>
    </>
  )
}
